import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import {
    autoFormatDateInput,
    autoFormatYearInput,
    buildYearPickerOptions,
    dateFromStoredString,
    formatStoredDateForDisplay,
    formatStoredYearForDisplay,
    getYearPickerMax,
    parseDateInput,
    parseYearInput,
    YEAR_PICKER_MIN,
} from '../../../utils/dateInputUtils';
import { IconCalendar } from '@icons';

const toDisplayText = (storedValue: string, mode: 'date' | 'year'): string => {
    if (!storedValue) return '';

    if (mode === 'year') {
        return formatStoredYearForDisplay(storedValue);
    }

    const display = formatStoredDateForDisplay(storedValue);
    return display === '-' ? '' : display;
};

const toStoredDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export type DateFieldMode = 'date' | 'year';

export interface DateFieldProps {
    /** Stored value: `YYYY-MM-DD` for date mode, `YYYY` for year mode */
    value: string;
    onChange: (value: string) => void;
    /** Defaults to `date`. Use `year` for year-only pickers. */
    mode?: DateFieldMode;
    /** Minimum selectable year when `mode="year"`. Defaults to 1990. */
    minYear?: number;
    /** Maximum selectable year when `mode="year"`. Defaults to current year. */
    maxYear?: number;
    placeholder?: string;
    disabled?: boolean;
    hasError?: boolean;
    className?: string;
    ariaLabel?: string;
    inputId?: string;
}

export const DateField = ({
    value,
    onChange,
    mode = 'date',
    minYear = YEAR_PICKER_MIN,
    maxYear = getYearPickerMax(),
    placeholder,
    disabled = false,
    hasError = false,
    className,
    ariaLabel,
    inputId,
}: DateFieldProps) => {
    const generatedId = useId();
    const resolvedInputId = inputId || generatedId;
    const listboxId = `${resolvedInputId}-year-listbox`;
    const rootRef = useRef<HTMLDivElement>(null);
    const [displayText, setDisplayText] = useState(() => toDisplayText(value, mode));
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    const resolvedPlaceholder = placeholder ?? (mode === 'year' ? 'YYYY' : 'MM/DD/YYYY');
    const yearOptions = useMemo(
        () => buildYearPickerOptions(minYear, maxYear),
        [minYear, maxYear],
    );
    const selectedYear = mode === 'year' ? formatStoredYearForDisplay(value) : '';

    useEffect(() => {
        setDisplayText(toDisplayText(value, mode));
    }, [value, mode]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (rootRef.current?.contains(event.target as Node)) return;
            setIsPickerOpen(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const rootClassName = [
        'date-field',
        mode === 'year' ? 'is-year-mode' : '',
        isPickerOpen ? 'is-open' : '',
        disabled ? 'is-disabled' : '',
        hasError ? 'has-error' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const handleDateInputChange = (nextValue: string) => {
        const formatted = autoFormatDateInput(nextValue, displayText);
        setDisplayText(formatted);

        if (!formatted) {
            onChange('');
            return;
        }

        const parsed = parseDateInput(formatted);
        if (parsed) {
            onChange(parsed);
        }
    };

    const handleYearInputChange = (nextValue: string) => {
        const formatted = autoFormatYearInput(nextValue, displayText);
        setDisplayText(formatted);

        if (!formatted) {
            onChange('');
            return;
        }

        const parsed = parseYearInput(formatted, minYear, maxYear);
        if (parsed) {
            onChange(parsed);
        }
    };

    const handleYearInputBlur = () => {
        if (!displayText) {
            onChange('');
            return;
        }

        const parsed = parseYearInput(displayText, minYear, maxYear);
        if (parsed) {
            onChange(parsed);
            setDisplayText(parsed);
            return;
        }

        setDisplayText(toDisplayText(value, mode));
    };

    const handleCalendarSelect = (date: Date | undefined) => {
        if (!date) return;

        const storedDate = toStoredDateString(date);
        onChange(storedDate);
        setDisplayText(toDisplayText(storedDate, mode));
        setIsPickerOpen(false);
    };

    const handleYearSelect = (year: number) => {
        const storedYear = String(year);
        onChange(storedYear);
        setDisplayText(storedYear);
        setIsPickerOpen(false);
    };

    return (
        <div ref={rootRef} className={rootClassName}>
            <div className="date-field-control">
                <button
                    type="button"
                    className="date-field-calendar-btn"
                    disabled={disabled}
                    aria-label={mode === 'year' ? 'Open year picker' : 'Open calendar'}
                    aria-expanded={isPickerOpen}
                    aria-controls={mode === 'year' ? listboxId : undefined}
                    onClick={() => {
                        if (disabled) return;
                        setIsPickerOpen((open) => !open);
                    }}
                >
                    <span aria-hidden="true">
                        <IconCalendar />
                    </span>
                </button>
                <input
                    id={resolvedInputId}
                    type="text"
                    inputMode={mode === 'year' ? 'numeric' : 'text'}
                    className="date-field-input"
                    value={displayText}
                    placeholder={resolvedPlaceholder}
                    disabled={disabled}
                    autoComplete="off"
                    aria-label={ariaLabel}
                    onChange={(event) =>
                        mode === 'year'
                            ? handleYearInputChange(event.target.value)
                            : handleDateInputChange(event.target.value)
                    }
                    onBlur={mode === 'year' ? handleYearInputBlur : undefined}
                    onFocus={() => {
                        if (!disabled) setIsPickerOpen(false);
                    }}
                />
            </div>
            {isPickerOpen && !disabled && mode === 'date' && (
                <div className="date-field-dropdown">
                    <DayPicker
                        mode="single"
                        selected={value ? dateFromStoredString(value) : undefined}
                        defaultMonth={value ? dateFromStoredString(value) : new Date()}
                        onSelect={handleCalendarSelect}
                        captionLayout="dropdown"
                        fixedWeeks
                        showOutsideDays={false}
                    />
                </div>
            )}
            {isPickerOpen && !disabled && mode === 'year' && (
                <div className="date-field-dropdown date-field-year-dropdown" id={listboxId} role="listbox" aria-label="Select year">
                    {yearOptions.map((year) => (
                        <button
                            key={year}
                            type="button"
                            role="option"
                            aria-selected={selectedYear === String(year)}
                            className={`date-field-year-option${selectedYear === String(year) ? ' is-selected' : ''}`}
                            onClick={() => handleYearSelect(year)}
                        >
                            {year}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
